import { ReactComponent as CloseIcon } from '@app/assets/x.svg';
import ChampionAllPlansDialogPanel from '@app/domains/champion/components/ChampionAllPlansDialogPanel';
import ChampionBenefitDialogPanel from '@app/domains/champion/components/ChampionBenefitDialogPanel';
import type { ChampionPurchaseDialogProps, ChampionPurchaseDialogViews } from '@app/domains/champion/types';
import { convertMinusException } from '@app/domains/champion/utils/convertMinusException';
import { noop } from '@app/domains/common/utils';
import SubscribeDialog from '@app/domains/subscription/containers/SubscribeDialog';
import { Dialog } from '@mui/material';
import clsx from 'clsx';
import { useMemo, useState } from 'react';

export default function ChampionPurchaseDialog({
    on,
    novelId,
    novelName,
    activeChampionSubscription,
    championPlans,
    initialView,
    onClickSubscribePlan,
    onSubscribePlan,
    onClose,
}: ChampionPurchaseDialogProps) {
    const [currentView, setCurrentView] = useState<ChampionPurchaseDialogViews>(initialView);
    const [subscribeDialogOpened, setSubscribeDialogOpened] = useState(false);
    const dialogClasses = getDialogClasses(currentView);
    const sortedChampionPlans = useMemo(() => {
        const championPlansCopy = [...championPlans];
        championPlansCopy.sort(
            (a, b) => convertMinusException(a.advanceChapterCount) - convertMinusException(b.advanceChapterCount)
        );
        return championPlansCopy;
    }, [championPlans]);
    const cheapestChampionPlan = sortedChampionPlans[0];
    return (
        <>
            <Dialog open maxWidth={false} classes={dialogClasses}>
                <CloseIcon
                    onClick={onClose}
                    className="absolute right-15 top-15 inline-block cursor-pointer text-gray-400"
                />
                <div className="h-full w-full overflow-y-auto overflow-x-hidden styled-scrollbar">
                    <section data-posthog-params={JSON.stringify({ on, novelId, novelName })}>
                        {currentView === 'benefit' && (
                            <div className="px-16">
                                <ChampionBenefitDialogPanel
                                    novelName={novelName}
                                    championPlan={cheapestChampionPlan}
                                    onClickMore={() => {
                                        setCurrentView('all-plans');
                                    }}
                                    onClickSubscribe={plan => {
                                        onClickSubscribePlan?.(plan);
                                        setSubscribeDialogOpened(true);
                                    }}
                                />
                            </div>
                        )}
                        {currentView === 'all-plans' && (
                            <div className="relative px-20 pb-20">
                                <ChampionAllPlansDialogPanel
                                    novelId={novelId}
                                    novelName={novelName}
                                    championPlans={sortedChampionPlans}
                                    activeChampionSubscription={activeChampionSubscription}
                                    onSubscribePlan={onSubscribePlan}
                                />
                            </div>
                        )}
                    </section>
                </div>
            </Dialog>
            <SubscribeDialog
                opened={subscribeDialogOpened}
                onClose={() => {
                    setSubscribeDialogOpened(false);
                }}
                cancelOpened={false}
                onCancelClose={noop}
                plan={cheapestChampionPlan}
                mode={'subscribe'}
                onSubscribe={onSubscribePlan}
            />
        </>
    );
}

const getDialogClasses = (currentView: ChampionPurchaseDialogViews) => {
    return {
        paper: clsx(
            'm-0 w-[calc(100vw-16px)] dark:bg-gray-850 bg-none max-w-none',
            { '!max-w-[359px] pt-30 pb-25': currentView === 'benefit' },
            {
                'md:max-w-[690px] max-h-[calc(100%-40px)] sm:max-h-[min(94.40%,860px)] pt-36':
                    currentView === 'all-plans',
            }
        ),
    };
};
